import {
  IAssignBuilding,
  IAssignedBuildingData,
  IContractInfo,
} from "../interfaces/IAssignBuilding";
import { convertToISO, toCamel, toPascal } from "../../utils/helpers";
import {
  getByPropertyId,
  getByContractNumber,
  saveAPI,
  unAssignBuildingAPI,
  contactStatusAPI,
  getAssignedBuildingsAPI,
  retrieveBuildingInfoByIdAPI,
  getEngineerTechnicianAPI,
  validateContractNumberUniquenessAPI,
  validateUnassignSystemContractAPI,
  unAssingSystemServicesAPI,
  retrieveSystemServicesByBuildingAPI,
  validateSystemServiceAPI,
} from "src/api/assignBuildingAPI";
import {
  createListenerMiddleware,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { handleResponse } from "src/services/apiServices";
import { trackPromise } from "react-promise-tracker";
import { NUMBER_OF_ROWS_PER_PAGE } from "src/resources/constant";
import { getSortedContractNumber } from "src/utils/common";
import moment from "moment";

export const save = (onSuccess: any, onFailure: any) => {
  return async (dispatch: any, getState: any) => {
    const language = getState().lookup.language;
    const isSystemsPopupOpened = getState().assignBuilding.isSystemsPopupOpened;
    const contractDetails = getState().assignBuilding.contractInfo;
    const { bMCProfile } = getState();
    const { assignBuilding } = getState();
    const pascalObj = toPascal(contractDetails);
    pascalObj["BMCCompanyID"] = bMCProfile.BMCData.id;
    pascalObj["PropertyGuid"] = assignBuilding.buildingInfo.propertyId;
    const preventiveDates = assignBuilding.contractInfo.preventiveDates;
    let formattedPreventiveDates = [];
    if (preventiveDates) {
      for (const index in preventiveDates) {
        const isoDate = convertToISO(preventiveDates[index]);
        const value = moment(isoDate).format("YYYY-MM-DDTHH:mm:ss[Z]");
        formattedPreventiveDates.push(convertToISO(value));
      }
    }
    pascalObj["PreventiveDates"] = formattedPreventiveDates;
    return await trackPromise(
      saveAPI(pascalObj)
        .then((res) => {
          const invoiceGuid = res?.data?.InvoiceId;
          if (isSystemsPopupOpened) {
            //set selected contract with the updated one
            dispatch(
              retrieveBuildingInfoById(
                assignBuilding.buildingInfo.propertyNumber,
                contractDetails.amcContractNumber
              )
            );
          }
          if (handleResponse(res, language)) {
            onSuccess?.(invoiceGuid);
          }
        })
        .catch((err) => {
          onFailure?.(err);
          handleResponse(err, language);
        })
    );
  };
};

export const searchByPropertyId = (propertyId: string) => {
  return async (dispatch: any, getState: any) => {
    return trackPromise(
      getByPropertyId(propertyId).then((res) => {
        const responseInCamelCase = toCamel(res.data);
        dispatch(setAssignedBuildingDetails(responseInCamelCase));
        return responseInCamelCase;
      })
    ).catch((error: any) => {
      handleResponse(error);
    });
  };
};

export const searchByContractNumber = () => {
  return async (dispatch: any, getState: any) => {
    const { assignBuilding } = getState();
    await trackPromise(
      getByContractNumber(assignBuilding.buildingInfo.propertyId).then(
        (res) => {
          // change prop to contract number
          const responseInCamelCase = toCamel(res.data);
          dispatch(setBuildingInfo(responseInCamelCase));
        }
      )
    );
  };
};

function mapSelectedSystems(responseInCamelCase: IContractInfo, systems: any) {
  let selected = responseInCamelCase.contractSystemServicesInfo;
  let selectedSystems: any[] = [];
  selected.forEach((ss) => {
    var system = systems.find(
      (se: { SystemServiceId: any }) =>
        se.SystemServiceId == ss.bmcContractServicesId
    );
    if (system) {
      system.make = ss.make;
      system.model = ss.model;
      system.quantity = ss.quantity;
      selectedSystems.push(system);
    }
  });
  return selectedSystems;
}
export const getContractStatus = () => {
  return async (dispatch: any, getState: any) => {
    await trackPromise(
      contactStatusAPI().then((res) => {
        dispatch(setContractStatus(res) as any);
      })
    );
  };
};

export const unAssignBuilding = (
  propertyId: string,
  justification: string,
  noOfActiveContracts: number
) => {
  return async (dispatch: any, getState: any) => {
    const payload = {
      PropertyId: propertyId,
      Justification: justification,
      NoOfActiveContract: noOfActiveContracts,
    };
    return trackPromise(
      unAssignBuildingAPI(payload).then((res) => {
        const responseInCamelCase = toCamel(res.data);
        return responseInCamelCase;
      })
    );
  };
};

export const validateContractNumberUniqueness = (contractNumber: string) => {
  return async (dispatch: any, getState: any) => {
    return await trackPromise(
      validateContractNumberUniquenessAPI(contractNumber)
        .then((res) => {
          return res;
        })
        .catch((err) => {
          handleResponse(err);
        })
    );
  };
};

export const getAssignedBuildings = (forExport = false) => {
  return async (dispatch: any, getState: any) => {
    const {
      assignBuilding: { filterData, gridCurrentPage, numberOfRowsPerPage },
    } = getState();
    const currentPage = gridCurrentPage + 1;
    const data = {
      ...toPascal({ ...filterData }),
      PageNumber: currentPage,
      PageCount: numberOfRowsPerPage,
    };
    if (forExport) {
      data.PageNumber = 1;
      data.PageCount = numberOfRowsPerPage * currentPage;
    }

    await trackPromise(
      getAssignedBuildingsAPI(data).then((res) => {
        dispatch(setTotalRowsCount(res?.data?.TotalRowsCount || 0));
        const data = toCamel(res?.data?.BuildingList || []);
        if (forExport) {
          dispatch(setExportData(data));
        } else {
          dispatch(setAssignedBuilding(data as []));
        }
      })
    );
  };
};

export const getEngineerTechnician = (forExport = false) => {
  return async (dispatch: any, getState: any) => {
    const {
      dashboard: { filterData },
    } = getState();
    const data: string[] = filterData?.propertyId?.map(
      (property: any) => property.propertyId
    );
    getEngineerTechnicianAPI(data).then((res: any) => {
      const data = toCamel(res?.data?.ContractsBySupervisorNames || []);
      dispatch(setEngineerTechnician(data as []));
    });
  };
};

export const retrieveBuildingInfoById = (
  propertyId: string,
  selectedContractNumber: string = ""
) => {
  return async (dispatch: any, getState: any) => {
    const contractStatuses = getState().lookup.contractStatuses;
    trackPromise(
      retrieveBuildingInfoByIdAPI(propertyId).then((response) => {
        const responseInCamelCase = toCamel(response.data);
        responseInCamelCase["contractDetails"] = getSortedContractNumber(
          responseInCamelCase.contractInfo,
          contractStatuses
        );
        let selectedContract = {};
        if (selectedContractNumber !== "") {
          selectedContract = responseInCamelCase.contractInfo.find(
            (contract: { amcContractNumber: string }) =>
              contract.amcContractNumber === selectedContractNumber
          );
        } else {
          selectedContract = responseInCamelCase["contractDetails"][0];
        }
        responseInCamelCase["selectedContract"] = selectedContract;

        dispatch(setBMCBuildingInfo(responseInCamelCase));

        responseInCamelCase.contractInfo = { ...selectedContract };
        dispatch(setAssignedBuildingDetails(responseInCamelCase));
      })
    );
  };
};

export const validateUnassignSystemContract = (
  propertyId: any,
  systemServiceList: any
) => {
  return async (dispatch: any, getState: any) => {
    const payload = { propertyId, systemServiceList };
    return trackPromise(validateUnassignSystemContractAPI(payload));
  };
};

export const unAssignSystemServices = (
  propertyId: any,
  systemServiceList: any
) => {
  return async (dispatch: any, getState: any) => {
    const payload = { propertyId, systemServiceList };
    return trackPromise(unAssingSystemServicesAPI(payload));
  };
};

export const retrieveSystemServicesByBuilding = (propertyId: string) => {
  return async (dispatch: any, getState: any) => {
    return trackPromise(
      retrieveSystemServicesByBuildingAPI(propertyId).then((res) => {
        dispatch(setUnassignBuildingPopupSystems(res.data.SystemServices));
        return res;
      })
    );
  };
};
export const validateSystemService = (
  propertyId: string,
  contractId: string | null,
  systemServiceId: string
) => {
  return async (dispatch: any, getState: any) => {
    const payload = {
      PropertyId: propertyId,
      ContractId: contractId,
      SystemServiceId: systemServiceId,
    };
    return trackPromise(validateSystemServiceAPI(payload)).then((res) => {
      const responseInCamelCase = toCamel(res.data);
      return responseInCamelCase;
    });
  };
};

const initialState: IAssignBuilding = {
  contractInfo: {
    id: "",
    contractId: "",
    amcContractNumber: "",
    contractTypeId: null,
    startDate: "",
    endDate: "",
    civilDefenseContractApprovalStatusId: 0,
    engineerTechnicianName: "",
    technicianMobileNumber: "",
    technicianEmail: "",
    technicianAlternateNumber: null,
    managerSupervisorName: "",
    managerMobileNumber: "",
    managerEmail: "",
    managerAlternateNumber: null,
    amcCopy: {
      fileName: null,
      fileExtension: null,
      fileBase64: null,
      subject: null,
    },
    contractSystemServicesInfo: [],
    preventiveDates: [],
    isNew: true,
    bmcCompanyID: "",
    propertyGuid: "",
    statusId: null,
  },
  originalAppointments: [],
  buildingDetails: {},
  contractDetails: {},
  propertyId: "",
  currentStep: "",
  contractTypes: [],
  contractStatus: [],
  assignedBuildingData: [],
  assignedBuildingDetailsPopupData: {
    caseId: 0,
    status: "",
    statusReason: "",
  },
  unassignedBuildingDetailsPopupData: {
    isUnAssignBuildingPopupOpen: false,
    systemServicesIds: [],
  },
  assignedBuildingSearchText: "",
  filterData: {
    propertyNumber: "",
    propertyId: "",
    contractNumber: "",
    sortColumn: "buildingName",
    isAscending: true,
  },
  bmcBuildingInfo: {
    buildingInfo: {
      name: "",
      propertyId: "",
      propertyNumber: "",
      buildingName: "",
      address1_Longitude: "",
      address1_Latitude: "",
      serviceTerritoryId: "",
      address1_Name: "",
      emirateId: "",
      cityId: "",
    },
    contractDetails: [],
    selectedContract: undefined,
  },
  buildingInfo: {
    propertyId: "",
    propertyNumber: "",
    buildingName: "",
    name: "",
    address1_Longitude: "",
    address1_Latitude: "",
    serviceTerritoryId: "",
    address1_Name: "",
    emirateId: "",
    cityId: "",
  },
  isContractPopupOpened: false,
  isSystemsPopupOpened: false,
  isConsentChecked: false,
  isNewMode: true,
  totalRowsCount: 0,
  gridCurrentPage: 0,
  numberOfRowsPerPage: NUMBER_OF_ROWS_PER_PAGE,
  exportData: [],
  engineerTechnicianData: [],
};

const assignBuildingSlice = createSlice({
  name: "assignBuilding",
  initialState,
  reducers: {
    setIsContractPopupOpened: (state, action) => {
      state.isContractPopupOpened = action.payload;
    },
    setIsSystemsPopupOpened: (state, action) => {
      state.isSystemsPopupOpened = action.payload;
    },
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },
    setGridCurrentPage: (state, action) => {
      state.gridCurrentPage = action.payload;
    },
    setContractStatus: (state, action) => {
      state.contractTypes = action.payload;
    },
    setTotalRowsCount: (state, action) => {
      state.totalRowsCount = action.payload;
    },
    setAssignedBuildingDetails: (state, action) => {
      state.buildingInfo = action.payload.buildingInfo;
      state.contractInfo = action.payload.contractInfo;
    },
    setBuildingInfo: (state, action) => {
      state.buildingInfo = action.payload.buildingInfo;
    },
    setOriginalAppointments: (state, action) => {
      state.originalAppointments = action.payload;
    },
    setContractInfo: (state, action) => {
      state.contractInfo = action.payload;
    },
    setIsConsentChecked: (state, action) => {
      state.isConsentChecked = action.payload;
    },
    setIsNewMode: (state, action) => {
      state.isNewMode = action.payload;
    },
    setAssignedBuilding: (state, action) => {
      state.assignedBuildingData = [...action.payload];
    },
    setEngineerTechnician: (state, action) => {
      state.engineerTechnicianData = [...action.payload];
    },
    clearAssignedBuildingDetailsPopupData: (state) => {
      state.assignedBuildingDetailsPopupData =
        initialState.assignedBuildingDetailsPopupData;
    },
    setAssignedBuildingPopupData: (
      state,
      action: {
        payload: { field: string; value: string | number };
        type: string;
      }
    ): void => {
      state.assignedBuildingDetailsPopupData = {
        ...state.assignedBuildingDetailsPopupData,
        [action.payload.field]: action.payload.value,
      };
    },
    clearAssignedBuildingPopupData: (state) => {
      state.assignedBuildingDetailsPopupData =
        initialState.assignedBuildingDetailsPopupData;
    },
    setAssignedBuildingSearchText: (state, action) => {
      state.assignedBuildingSearchText = action.payload;
    },

    setFilterDataField: (state, action) => {
      state.filterData = {
        ...state.filterData,
        [action.payload.field]: action.payload.value,
      };
    },
    resetFilterData: (state) => {
      state.filterData = initialState.filterData;
      state.gridCurrentPage = initialState.gridCurrentPage;
    },
    setBMCBuildingInfo: (state, action) => {
      state.bmcBuildingInfo = { ...action.payload };
    },
    resetBuildingInfo: (state) => {
      state.buildingInfo = initialState.buildingInfo;
      state.contractInfo = initialState.contractInfo;
    },
    resetContractInfo: (state) => {
      state.contractInfo = initialState.contractInfo;
    },
    setSelectedContract: (state, action) => {
      state.bmcBuildingInfo.selectedContract = action.payload;
    },
    setNumberOfRowsPerPage: (state, action) => {
      state.numberOfRowsPerPage = action.payload;
    },
    setExportData: (state, action) => {
      state.exportData = action.payload;
    },
    setIsUnassignBuildingPopupOpen: (state, action) => {
      state.unassignedBuildingDetailsPopupData.isUnAssignBuildingPopupOpen =
        action.payload;
    },
    setUnassignBuildingPopupSystems: (state, action) => {
      state.unassignedBuildingDetailsPopupData.systemServicesIds =
        action.payload;
    },
  },
});

export const {
  setBuildingInfo,
  setOriginalAppointments,
  setCurrentStep,
  setContractInfo,
  setContractStatus,
  setFilterDataField,
  resetFilterData,
  resetBuildingInfo,
  resetContractInfo,
  setAssignedBuildingSearchText,
  setAssignedBuildingPopupData,
  clearAssignedBuildingDetailsPopupData,
  setAssignedBuilding,
  setEngineerTechnician,
  setIsConsentChecked,
  setIsNewMode,
  setBMCBuildingInfo,
  setAssignedBuildingDetails,
  setSelectedContract,
  setIsContractPopupOpened,
  setIsSystemsPopupOpened,
  setTotalRowsCount,
  setNumberOfRowsPerPage,
  setGridCurrentPage,
  setExportData,
  setIsUnassignBuildingPopupOpen,
  setUnassignBuildingPopupSystems,
} = assignBuildingSlice.actions;
export default assignBuildingSlice.reducer;

const makeFilterAssignedBuildingsSelector = (
  dataSourceSelector: (state: any) => any
) =>
  createSelector(
    dataSourceSelector,
    (state: any) => state.assignBuilding.assignedBuildingSearchText,
    (assignedBuildingData, assignedBuildingSearchText) => {
      return getAssignedBuildingFilteredData(
        assignedBuildingData,
        assignedBuildingSearchText
      );
    }
  );

export const selectAssignedBuildings = makeFilterAssignedBuildingsSelector(
  (state: any) => state.assignBuilding.assignedBuildingData
);

export const selectAssignedBuildingsForExport =
  makeFilterAssignedBuildingsSelector(
    (state: any) => state.assignBuilding.exportData
  );

//listener that listens to setGridCurrentPage and dispatch getPreventiveAppointment
const getAssignedBuildingsListenerMiddleware = createListenerMiddleware();

getAssignedBuildingsListenerMiddleware.startListening({
  actionCreator: setGridCurrentPage,
  effect: async (action, listenerApi) => {
    listenerApi.dispatch(getAssignedBuildings());
  },
});

export { getAssignedBuildingsListenerMiddleware };
function getAssignedBuildingFilteredData(
  assignedBuildingData: any,
  assignedBuildingSearchText: any
): any {
  return assignedBuildingData.filter(
    (assignedBuilding: IAssignedBuildingData) => {
      return Object.entries(assignedBuilding)
        .filter(([key]) => key !== "propertyId")
        .some((field) => {
          return field[1]
            .toString()
            .toLowerCase()
            .includes(assignedBuildingSearchText.toLowerCase());
        });
    }
  );
}
